/**
 * Safe Data Utilities
 *
 * Defensive utilities to prevent SPA crashes from unsafe data access patterns.
 * Addresses NocoDB #4600: sidebar-nav defensive guard against ?.data.filter() anti-pattern.
 *
 * @module safe-data
 * @priority medium
 * @compliance NocoDB #4600
 */
/**
 * Safely get array from potentially undefined/nullable data.
 *
 * @example
 * // BEFORE (crash-prone):
 * const items = data?.data.filter(item => item.active);
 *
 * // AFTER (safe):
 * const items = safeFilter(data?.data, item => item.active);
 *
 * @param data - Potentially undefined or non-array data
 * @param predicate - Filter predicate
 * @param fallback - Fallback array if data is not an array (default: [])
 * @returns Filtered array or fallback
 */
export function safeFilter(data, predicate, fallback = []) {
    if (!Array.isArray(data)) {
        return fallback;
    }
    return data.filter(predicate);
}
/**
 * Safely get array from API response data structure.
 *
 * @example
 * // BEFORE (crash-prone):
 * const items = response?.data?.items.filter(item => item.active);
 *
 * // AFTER (safe):
 * const items = safeArray(response?.data?.items);
 *
 * @param data - Potentially undefined or non-array data
 * @param fallback - Fallback array if data is not an array (default: [])
 * @returns Array or fallback
 */
export function safeArray(data, fallback = []) {
    if (!Array.isArray(data)) {
        return fallback;
    }
    return data;
}
/**
 * Safely count elements in an array that match a predicate.
 *
 * @example
 * // BEFORE (crash-prone):
 * const count = response?.data.filter(item => item.status === 'error').length ?? 0;
 *
 * // AFTER (safe):
 * const count = safeCount(response?.data, item => item.status === 'error');
 *
 * @param data - Potentially undefined or non-array data
 * @param predicate - Filter predicate
 * @param fallback - Fallback count if data is not an array (default: 0)
 * @returns Count of matching elements or fallback
 */
export function safeCount(data, predicate, fallback = 0) {
    if (!Array.isArray(data)) {
        return fallback;
    }
    return data.filter(predicate).length;
}
/**
 * Safely map over an array that may not exist.
 *
 * @example
 * const names = safeMap(user?.friends, friend => friend.name);
 *
 * @param data - Potentially undefined or non-array data
 * @param mapper - Map function
 * @param fallback - Fallback array if data is not an array (default: [])
 * @returns Mapped array or fallback
 */
export function safeMap(data, mapper, fallback = []) {
    if (!Array.isArray(data)) {
        return fallback;
    }
    return data.map(mapper);
}
/**
 * Safely find an element in an array that may not exist.
 *
 * @example
 * const user = safeFind(users?.data, user => user.id === id);
 *
 * @param data - Potentially undefined or non-array data
 * @param predicate - Find predicate
 * @param fallback - Fallback value if not found (default: undefined)
 * @returns Found element or fallback
 */
export function safeFind(data, predicate, fallback) {
    if (!Array.isArray(data)) {
        return fallback;
    }
    const result = data.find(predicate);
    return result !== undefined ? result : fallback;
}
/**
 * Safely check if array includes a value.
 *
 * @example
 * const hasAdmin = safeIncludes(roles?.data, 'admin');
 *
 * @param data - Potentially undefined or non-array data
 * @param value - Value to check for
 * @returns True if array includes value, false otherwise
 */
export function safeIncludes(data, value) {
    if (!Array.isArray(data)) {
        return false;
    }
    return data.includes(value);
}
/**
 * Type guard for arrays that filters out nullish values.
 *
 * @param value - Value to check
 * @returns True if value is not null or undefined
 */
export function notNullish(value) {
    return value !== null && value !== undefined;
}
/**
 * Filter array and remove nullish values in one pass.
 *
 * @example
 * const items = filterNotNullish(data?.items);
 * // items is T[] where all values are non-null and non-undefined
 *
 * @param data - Potentially undefined/null array
 * @returns Array with nullish values removed
 */
export function filterNotNullish(data) {
    if (!Array.isArray(data)) {
        return [];
    }
    return data.filter(notNullish);
}
//# sourceMappingURL=safe-data.js.map